import type {
    BreakpointId,
    CoreExportFormat,
    ComponentLayout,
    GridContainerLayout,
    FlexContainerLayout,
    GridItemLayout,
    FlexItemLayout,
    FixedItemLayout,
    SingleLayoutData,
    ContainerLayouts,
    ItemLayouts,
    VariantSplit,
    SPXReferenceDefinition,
} from '@wix/document-services-types'
import type { CssFile } from '../../../types'
import type { CssDeclaration, CssRoot } from '../../utils/css/types'
import { generateCssFile } from '../../utils/css/cssFileBuilder'
import { buildCssDeclaration } from '../../utils/css/utils'
import { buildSpxReferenceDeclarations } from '../../utils/css/spxTransformer'
import { traverseComponents } from '../../../traversal/traverse'
import {
    serializeSize,
    serializeHeight,
    serializeAspectRatio,
    serializeRotation,
    serializeVisibility,
    serializeGridTracks,
    serializeGridLine,
    serializeFlexDirection,
    serializeFlexWrap,
    serializeContentAlignment,
    serializeAlignment,
    serializeAutoFlow,
    serializeOverflow,
    serializeMargins,
    serializePadding,
} from './valueSerializers'
import _ from 'lodash'
import { buildSelector } from '../../utils/css/cssSelectors'
import { processVariantSplit } from '../../utils/variantUtils'
import { buildCssAst, CssRecord } from '../../utils/css/ast/astBuilder'
import { getBreakpointData } from '../../utils/breakpoints'

const addDeclaration = (declarations: CssDeclaration[], prop: string, value: string | undefined): void => {
    if (value) {
        declarations.push(buildCssDeclaration(prop, value))
    }
}

const addDeclarations = (declarations: CssDeclaration[], record: Record<string, string | undefined>): void => {
    _.forEach(record, (value, prop) => {
        addDeclaration(declarations, prop, value)
    })
}

const serializeComponentLayout = (
    declarations: CssDeclaration[],
    componentLayout: ComponentLayout | undefined,
): void => {
    if (!componentLayout) {
        return
    }

    addDeclaration(declarations, 'width', serializeSize(componentLayout.width as any))
    addDeclaration(declarations, 'height', serializeHeight(componentLayout.height))
    addDeclaration(declarations, 'aspect-ratio', serializeAspectRatio(componentLayout.height))

    if (componentLayout.height) {
        addDeclaration(declarations, '--height-type', componentLayout.height.type)
    }

    addDeclaration(declarations, 'min-width', serializeSize(componentLayout.minWidth))
    addDeclaration(declarations, 'min-height', serializeSize(componentLayout.minHeight))
    addDeclaration(declarations, 'max-width', serializeSize(componentLayout.maxWidth))
    addDeclaration(declarations, 'max-height', serializeSize(componentLayout.maxHeight))
    addDeclaration(declarations, 'rotate', serializeRotation(componentLayout.rotationInDegrees))
    addDeclaration(declarations, 'visibility', serializeVisibility(componentLayout.hidden))

    if (componentLayout.direction) {
        addDeclaration(declarations, 'direction', componentLayout.direction)
    }
}

const serializeGridItemLayout = (declarations: CssDeclaration[], itemLayout: GridItemLayout): void => {
    if (itemLayout.gridArea) {
        const { rowStart, rowEnd, columnStart, columnEnd } = itemLayout.gridArea
        addDeclaration(declarations, 'grid-row', serializeGridLine(rowStart, rowEnd))
        addDeclaration(declarations, 'grid-column', serializeGridLine(columnStart, columnEnd))
    }

    addDeclaration(declarations, 'align-self', serializeAlignment(itemLayout.alignSelf))
    addDeclaration(declarations, 'justify-self', serializeAlignment(itemLayout.justifySelf))
    addDeclarations(declarations, serializeMargins(itemLayout.margins))
}

const serializeFlexItemLayout = (declarations: CssDeclaration[], itemLayout: FlexItemLayout): void => {
    addDeclaration(declarations, 'align-self', serializeAlignment(itemLayout.alignSelf))

    if (!_.isUndefined(itemLayout.order)) {
        addDeclaration(declarations, 'order', String(itemLayout.order))
    }

    addDeclaration(declarations, 'flex-basis', serializeSize(itemLayout.basis))

    if (!_.isUndefined(itemLayout.grow)) {
        addDeclaration(declarations, 'flex-grow', String(itemLayout.grow))
    }

    if (!_.isUndefined(itemLayout.shrink)) {
        addDeclaration(declarations, 'flex-shrink', String(itemLayout.shrink))
    }
}

const serializeFixedItemLayout = (declarations: CssDeclaration[], itemLayout: FixedItemLayout): void => {
    if (itemLayout.position) {
        addDeclaration(declarations, 'position', itemLayout.position)
    }

    addDeclaration(declarations, 'top', serializeSize(itemLayout.top))
    addDeclaration(declarations, 'bottom', serializeSize(itemLayout.bottom))
    addDeclaration(declarations, 'left', serializeSize(itemLayout.left))
    addDeclaration(declarations, 'right', serializeSize(itemLayout.right))
    addDeclaration(declarations, 'align-self', serializeAlignment(itemLayout.alignSelf))
    addDeclaration(declarations, 'justify-self', serializeAlignment(itemLayout.justifySelf))
    addDeclarations(declarations, serializeMargins(itemLayout.margins))
}

const serializeItemLayout = (declarations: CssDeclaration[], itemLayout: ItemLayouts | {} | undefined): void => {
    if (!itemLayout || _.isEmpty(itemLayout)) {
        return
    }

    const layout = itemLayout as ItemLayouts
    addDeclaration(declarations, '--item-layout-type', layout.type)

    switch (layout.type) {
        case 'GridItemLayout':
            serializeGridItemLayout(declarations, layout as GridItemLayout)
            break
        case 'FlexItemLayout':
        case 'StackItemLayout':
        case 'OrganizerItemLayout':
            serializeFlexItemLayout(declarations, layout as FlexItemLayout)
            break
        case 'FixedItemLayout':
            serializeFixedItemLayout(declarations, layout as FixedItemLayout)
            break
    }
}

const serializeGridContainerLayout = (declarations: CssDeclaration[], containerLayout: GridContainerLayout): void => {
    addDeclaration(declarations, 'display', 'grid')
    addDeclaration(declarations, 'grid-template-columns', serializeGridTracks(containerLayout.columns))
    addDeclaration(declarations, 'grid-template-rows', serializeGridTracks(containerLayout.rows))
    addDeclaration(declarations, 'row-gap', serializeSize(containerLayout.rowGap))
    addDeclaration(declarations, 'column-gap', serializeSize(containerLayout.columnGap))
    addDeclaration(declarations, 'grid-auto-flow', serializeAutoFlow(containerLayout.autoFlow))
    addDeclaration(declarations, 'grid-auto-rows', serializeGridTracks(containerLayout.autoRows))
    addDeclaration(declarations, 'grid-auto-columns', serializeGridTracks(containerLayout.autoColumns))
    addDeclarations(declarations, serializePadding(containerLayout.padding))
    addDeclaration(declarations, 'overflow-x', serializeOverflow(containerLayout.overflowX))
    addDeclaration(declarations, 'overflow-y', serializeOverflow(containerLayout.overflowY))
}

const serializeFlexContainerLayout = (declarations: CssDeclaration[], containerLayout: FlexContainerLayout): void => {
    addDeclaration(declarations, 'display', 'flex')
    addDeclaration(declarations, 'flex-direction', serializeFlexDirection(containerLayout.direction))
    addDeclaration(declarations, 'flex-wrap', serializeFlexWrap(containerLayout.wrap))
    addDeclaration(declarations, 'justify-content', serializeContentAlignment(containerLayout.justifyContent))
    addDeclaration(declarations, 'align-items', serializeAlignment(containerLayout.alignItems))
    addDeclaration(declarations, 'align-content', serializeContentAlignment(containerLayout.alignContent))
    addDeclaration(declarations, 'row-gap', serializeSize(containerLayout.rowGap))
    addDeclaration(declarations, 'column-gap', serializeSize(containerLayout.columnGap))
    addDeclarations(declarations, serializePadding(containerLayout.padding))
    addDeclaration(declarations, 'overflow-x', serializeOverflow(containerLayout.overflowX))
    addDeclaration(declarations, 'overflow-y', serializeOverflow(containerLayout.overflowY))
}

const serializeContainerLayout = (
    declarations: CssDeclaration[],
    containerLayout: ContainerLayouts | {} | undefined,
): void => {
    if (!containerLayout || _.isEmpty(containerLayout)) {
        return
    }

    const layout = containerLayout as ContainerLayouts
    addDeclaration(declarations, '--container-layout-type', layout.type)

    switch (layout.type) {
        case 'GridContainerLayout':
            serializeGridContainerLayout(declarations, layout as GridContainerLayout)
            break
        case 'FlexContainerLayout':
        case 'StackContainerLayout':
        case 'OrganizerContainerLayout':
            serializeFlexContainerLayout(declarations, layout as FlexContainerLayout)
            break
    }
}

const serializeSpxDefinition = (declarations: CssDeclaration[], spx: SPXReferenceDefinition | undefined): void => {
    const spxDeclarations = buildSpxReferenceDeclarations(spx)

    _.forEach(spxDeclarations, (value, key) => {
        addDeclaration(declarations, key, value)
    })
}

const appendLayoutRecord = (
    records: CssRecord[],
    selector: string,
    layouts: Partial<SingleLayoutData> | undefined,
    breakpointId?: BreakpointId,
): void => {
    if (!layouts) {
        return
    }

    const declarations: CssDeclaration[] = []

    serializeSpxDefinition(declarations, layouts.spx)
    serializeComponentLayout(declarations, layouts.componentLayout)
    serializeItemLayout(declarations, layouts.itemLayout)
    serializeContainerLayout(declarations, layouts.containerLayout)

    if (declarations.length > 0) {
        records.push({ selector, breakpointId, type: 'style', declarations })
    }
}

const appendComponentLayoutRecords = (
    records: CssRecord[],
    selector: string,
    layout: VariantSplit<Partial<SingleLayoutData>> | undefined,
): void => {
    processVariantSplit(layout, {
        onDefault: value => appendLayoutRecord(records, selector, value),
        onVariant: (breakpointId, value) => appendLayoutRecord(records, selector, value, breakpointId),
    })
}

export const getLayoutRecords = (componentExport: CoreExportFormat): CssRecord[] => {
    const records: CssRecord[] = []

    traverseComponents(componentExport, {
        onComponent: ({ id, component }) => {
            const layout = component.layout as VariantSplit<Partial<SingleLayoutData>> | undefined
            appendComponentLayoutRecords(records, buildSelector({ type: 'component', componentId: id }), layout)
        },
    })

    return records
}

export const generateLayoutCssAst = (componentExport: CoreExportFormat): CssRoot =>
    buildCssAst(getLayoutRecords(componentExport), getBreakpointData(componentExport))

export const generateLayout = (componentExport: CoreExportFormat): CssFile =>
    generateCssFile(generateLayoutCssAst(componentExport))
